"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePostHog } from "@/lib/posthog/hooks"
import { randomUUID } from "@/lib/random-uuid"

export type ApplicationWorkflowType =
  | "ai_first"
  | "human_first"
  | "screening_question"
export type ApplicationDeviceType = "mobile" | "tablet" | "desktop"
export type ApplicationFunnelEvent =
  | "application_form_viewed"
  | "application_started"
  | "application_field_stage_reached"
  | "application_validation_failed"
  | "application_submission_reconciled"
  | "resume_upload_succeeded"
  | "resume_upload_failed"
  | "application_submitted"
  | "screening_completed"
  | "assessment_continued"

type ApplicationSource =
  | "job_board"
  | "indeed"
  | "indeed_dashboard"
  | "linkedin"
  | "jobstak"
  | "referral"
  | "craigslist"
  | "embed"
  | "meta"
  | "talent"
  | "jooble"
  | "adzuna"

export type ApplicationAcquisitionSource =
  | ApplicationSource
  | "direct"
  | "organic_search"
  | "paid_search"
  | "social"
  | "email"
  | "other_campaign"

type ApplicationFailureReason =
  | "file_too_large"
  | "http_error"
  | "network_error"

interface ApplicationEventExtras {
  failure_reason?: ApplicationFailureReason
  http_status?: number
  has_resume?: boolean
  field_stage?: "contact" | "profile" | "resume" | "messaging"
  validation_stage?: "contact" | "profile" | "messaging" | "other"
  error_count?: number
  event_variant?: "contact" | "profile" | "resume" | "messaging" | "other"
}

interface ApplicationFunnelContext {
  application_flow_id: string
  acquisition_source: ApplicationAcquisitionSource
  device_type: ApplicationDeviceType
  link_id: number
  org_slug: string | null
  source: ApplicationSource
  workflow_type: ApplicationWorkflowType
}

const APPLICATION_SOURCES = new Set<ApplicationSource>([
  "job_board",
  "indeed",
  "indeed_dashboard",
  "linkedin",
  "jobstak",
  "referral",
  "craigslist",
  "embed",
  "meta",
  "talent",
  "jooble",
  "adzuna",
])
const ACQUISITION_SOURCE_STORAGE_KEY = "hirealigned.application_acquisition_source"
const ACQUISITION_SESSION_IDLE_MS = 30 * 60 * 1000
const ACQUISITION_SESSION_MAX_MS = 24 * 60 * 60 * 1000
const APPLICATION_FLOW_STORAGE_PREFIX = "hirealigned.application_flow."
const APPLICATION_FLOW_MAX_MS = 24 * 60 * 60 * 1000
const APPLICATION_ACQUISITION_SOURCES = new Set<ApplicationAcquisitionSource>([
  ...APPLICATION_SOURCES,
  "direct",
  "organic_search",
  "paid_search",
  "social",
  "email",
  "other_campaign",
])

function normalizeApplicationSource(source?: string): ApplicationSource {
  const normalized = source?.trim().toLowerCase() as ApplicationSource | undefined
  return normalized && APPLICATION_SOURCES.has(normalized)
    ? normalized
    : "job_board"
}

const SEARCH_HOSTS = /(^|\.)(google|bing|yahoo|duckduckgo)\./i
const SOCIAL_HOSTS =
  /(^|\.)(linkedin|facebook|instagram|tiktok|x|twitter|reddit)\.com$/i

function normalizedCampaignValue(value: string | null): string {
  return value?.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "") ?? ""
}

export function deriveApplicationAcquisitionSource({
  source,
  search = "",
  referrer = "",
  origin = "",
}: {
  source?: string
  search?: string
  referrer?: string
  origin?: string
}): ApplicationAcquisitionSource {
  const explicit = source?.trim().toLowerCase() as ApplicationSource | undefined
  if (explicit && APPLICATION_SOURCES.has(explicit)) return explicit

  const params = new URLSearchParams(search)
  const utmSource = normalizedCampaignValue(params.get("utm_source"))
  const utmMedium = normalizedCampaignValue(params.get("utm_medium"))
  const hasCampaign = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "fbclid",
    "msclkid",
  ].some((key) => params.has(key))

  if (/^(cpc|ppc|paid|paidsearch|display)$/.test(utmMedium) || params.has("gclid")) {
    return "paid_search"
  }
  if (/^(social|paidsocial)$/.test(utmMedium) || params.has("fbclid")) return "social"
  if (/^(email|newsletter)$/.test(utmMedium)) return "email"
  if (APPLICATION_SOURCES.has(utmSource as ApplicationSource)) {
    return utmSource as ApplicationSource
  }
  if (/^(google|bing|yahoo|duckduckgo)$/.test(utmSource)) return "organic_search"
  if (/^(facebook|instagram|tiktok|twitter|x|reddit)$/.test(utmSource)) return "social"
  if (hasCampaign) return "other_campaign"

  if (!referrer) return "direct"
  try {
    const referrerUrl = new URL(referrer)
    if (origin && referrerUrl.origin === origin) return "job_board"
    if (SEARCH_HOSTS.test(referrerUrl.hostname)) return "organic_search"
    if (/(^|\.)linkedin\.com$/i.test(referrerUrl.hostname)) return "linkedin"
    if (SOCIAL_HOSTS.test(referrerUrl.hostname)) return "social"
    if (/(^|\.)indeed\.com$/i.test(referrerUrl.hostname)) return "indeed"
    if (/(^|\.)craigslist\.org$/i.test(referrerUrl.hostname)) return "craigslist"
    return "referral"
  } catch {
    return "direct"
  }
}

function hasCurrentCampaign(search: string): boolean {
  const params = new URLSearchParams(search)
  return [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "fbclid",
    "msclkid",
  ].some((key) => params.has(key))
}

interface StoredAcquisitionSource {
  source: ApplicationAcquisitionSource
  started_at: number
  last_seen_at: number
}

function readStoredAcquisitionSource(): StoredAcquisitionSource | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.sessionStorage.getItem(ACQUISITION_SOURCE_STORAGE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as Partial<StoredAcquisitionSource>
    const now = Date.now()
    if (
      !APPLICATION_ACQUISITION_SOURCES.has(
        stored.source as ApplicationAcquisitionSource
      ) ||
      typeof stored.started_at !== "number" ||
      typeof stored.last_seen_at !== "number" ||
      now - stored.last_seen_at > ACQUISITION_SESSION_IDLE_MS ||
      now - stored.started_at > ACQUISITION_SESSION_MAX_MS
    ) {
      window.sessionStorage.removeItem(ACQUISITION_SOURCE_STORAGE_KEY)
      return null
    }
    return stored as StoredAcquisitionSource
  } catch {
    return null
  }
}

function storeAcquisitionSource(
  source: ApplicationAcquisitionSource,
  startedAt = Date.now()
): void {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(
      ACQUISITION_SOURCE_STORAGE_KEY,
      JSON.stringify({
        source,
        started_at: startedAt,
        last_seen_at: Date.now(),
      } satisfies StoredAcquisitionSource)
    )
  } catch {
    // Storage may be unavailable in strict privacy modes; attribution remains
    // correct for the current page and safely falls back on the next page.
  }
}

export function resolveApplicationAcquisitionSource({
  source,
  search = "",
  referrer = "",
  origin = "",
}: {
  source?: string
  search?: string
  referrer?: string
  origin?: string
}): ApplicationAcquisitionSource {
  const explicit = source?.trim().toLowerCase() as ApplicationSource | undefined
  const hasExplicit = Boolean(explicit && APPLICATION_SOURCES.has(explicit))
  const hasCampaign = hasCurrentCampaign(search)
  let isExternalReferrer = false
  if (referrer) {
    try {
      isExternalReferrer = !origin || new URL(referrer).origin !== origin
    } catch {
      isExternalReferrer = false
    }
  }

  if (!hasExplicit && !hasCampaign && !isExternalReferrer) {
    const stored = readStoredAcquisitionSource()
    if (stored) {
      storeAcquisitionSource(stored.source, stored.started_at)
      return stored.source
    }
  }

  const derived = deriveApplicationAcquisitionSource({
    source,
    search,
    referrer,
    origin,
  })
  storeAcquisitionSource(derived)
  return derived
}

export function ApplicationAcquisitionTracker({ source }: { source?: string }) {
  useEffect(() => {
    resolveApplicationAcquisitionSource({
      source,
      search: window.location.search,
      referrer: document.referrer,
      origin: window.location.origin,
    })
  }, [source])
  return null
}

function applicationFlowStorageKey(linkId: number): string {
  return `${APPLICATION_FLOW_STORAGE_PREFIX}${linkId}`
}

interface StoredApplicationFlow {
  id: string
  created_at: number
  had_resume: boolean
  // Separate from `id` on purpose: `id` is the analytics join key and must stay
  // stable for the whole flow, while this is the server's idempotency
  // reservation and has to be reminted whenever the payload deliberately
  // changes (see clearPendingFlowResume). Absent on flows stored before this
  // field existed, which fall back to `id`.
  idempotency_key?: string
}

function storeApplicationFlow(
  linkId: number,
  flow: StoredApplicationFlow
): void {
  if (typeof window === "undefined") return
  try {
    // Keep this record deliberately narrow: it may survive a page remount, so
    // candidate fields, filenames, object URLs, hashes, and file bytes never
    // belong here.
    window.sessionStorage.setItem(
      applicationFlowStorageKey(linkId),
      JSON.stringify({
        id: flow.id,
        created_at: flow.created_at,
        had_resume: flow.had_resume,
        idempotency_key: flow.idempotency_key,
      } satisfies StoredApplicationFlow)
    )
  } catch {
    // Strict privacy modes can disable storage. The mounted form still keeps
    // the same state in memory.
  }
}

function createFreshApplicationFlow(linkId: number): StoredApplicationFlow {
  const flow = {
    id: randomUUID(),
    created_at: Date.now(),
    had_resume: false,
  }
  storeApplicationFlow(linkId, flow)
  return flow
}

function loadApplicationFlow(linkId: number): StoredApplicationFlow {
  if (typeof window !== "undefined") {
    try {
      const key = applicationFlowStorageKey(linkId)
      const raw = window.sessionStorage.getItem(key)
      if (raw) {
        const stored = JSON.parse(raw) as {
          id?: unknown
          created_at?: unknown
          had_resume?: unknown
          idempotency_key?: unknown
        }
        if (
          isUuid(stored.id) &&
          typeof stored.created_at === "number" &&
          stored.created_at <= Date.now() &&
          Date.now() - stored.created_at <= APPLICATION_FLOW_MAX_MS
        ) {
          return {
            id: stored.id,
            created_at: stored.created_at,
            had_resume: stored.had_resume === true,
            idempotency_key: isUuid(stored.idempotency_key)
              ? stored.idempotency_key
              : undefined,
          }
        }
        window.sessionStorage.removeItem(key)
      }
    } catch {
      // Fall through to a fresh in-memory flow.
    }
  }

  return createFreshApplicationFlow(linkId)
}

function getApplicationDeviceType(): ApplicationDeviceType {
  if (typeof window === "undefined") return "desktop"
  if (window.innerWidth < 768) return "mobile"
  if (window.innerWidth < 1024) return "tablet"
  return "desktop"
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    )
  )
}

export function useApplicationFunnel({
  linkId,
  orgSlug,
  source,
  workflowType,
}: {
  linkId: number
  orgSlug: string | null
  source?: string
  workflowType: ApplicationWorkflowType
}) {
  const { posthog } = usePostHog()
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  const [flow, setFlow] = useState<StoredApplicationFlow>(() =>
    loadApplicationFlow(linkId)
  )
  const [baseContext] = useState<
    Omit<ApplicationFunnelContext, "application_flow_id">
  >(() => ({
    acquisition_source: resolveApplicationAcquisitionSource({
      source,
      search: typeof window === "undefined" ? "" : window.location.search,
      referrer: typeof document === "undefined" ? "" : document.referrer,
      origin: typeof window === "undefined" ? "" : window.location.origin,
    }),
    device_type: getApplicationDeviceType(),
    link_id: linkId,
    org_slug: orgSlug,
    source: normalizeApplicationSource(source),
    workflow_type: workflowType,
  }))
  const context = useMemo<ApplicationFunnelContext>(
    () => ({
      ...baseContext,
      application_flow_id: flow.id,
    }),
    [baseContext, flow.id]
  )

  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer)
      timers.current.clear()
    },
    []
  )

  const captureApplicationEvent = useCallback(
    (event: ApplicationFunnelEvent, extras: ApplicationEventExtras = {}) => {
      if (!posthog) return

      let attempts = 0
      const captureWhenLoaded = () => {
        if ((posthog as { __loaded?: boolean }).__loaded) {
          posthog.capture(event, {
            ...context,
            ...extras,
            $insert_id: `application-funnel:${context.application_flow_id}:${event}${
              extras.event_variant ? `:${extras.event_variant}` : ""
            }`,
            analytics_schema_version: 1,
            event_source: "client",
          })
          return
        }
        if (attempts++ < 50) {
          const timer = setTimeout(() => {
            timers.current.delete(timer)
            captureWhenLoaded()
          }, 100)
          timers.current.add(timer)
        }
      }

      captureWhenLoaded()
    },
    [context, posthog]
  )

  const analyticsHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {
      "X-Application-Flow-ID": context.application_flow_id,
      "X-Acquisition-Source": context.acquisition_source,
      "X-Application-Source": context.source,
      "X-Application-Workflow-Type": context.workflow_type,
      "X-Application-Device-Type": context.device_type,
    }
    const distinctId = posthog?.get_distinct_id?.()
    const sessionId = posthog?.get_session_id?.()
    if (isUuid(distinctId)) headers["X-PostHog-Distinct-ID"] = distinctId
    if (isUuid(sessionId)) headers["X-PostHog-Session-ID"] = sessionId
    return headers
  }, [context, posthog])

  const clearApplicationFlowId = useCallback(() => {
    if (typeof window !== "undefined") {
      try {
        const key = applicationFlowStorageKey(linkId)
        const raw = window.sessionStorage.getItem(key)
        if (raw) {
          const stored = JSON.parse(raw) as { id?: unknown }
          if (stored.id === flow.id) {
            window.sessionStorage.removeItem(key)
          }
        }
      } catch {
        // Storage is only a remount-recovery enhancement. A failed cleanup does
        // not affect the completed application.
      }
    }
    setFlow((current) =>
      current.id === flow.id ? { ...current, had_resume: false } : current
    )
  }, [flow.id, linkId])

  const markApplicationFlowHadResume = useCallback(() => {
    if (flow.had_resume) return
    const updated = { ...flow, had_resume: true }
    storeApplicationFlow(linkId, updated)
    setFlow(updated)
  }, [flow, linkId])

  const clearPendingFlowResume = useCallback(() => {
    // Keep the same flow id so the client view/start events still join the
    // server-side submit. Only forget the pending resume so a candidate can
    // submit a fresh attempt without one.
    setFlow((current) => {
      if (!current.had_resume) return current
      // Mint a fresh idempotency key: the next submit deliberately drops
      // resume_url, so reusing the previous key would fail the server's
      // full-payload match and hard-409 a candidate whose first, resume-bearing
      // submit committed but whose response was lost.
      const updated = {
        ...current,
        had_resume: false,
        idempotency_key: randomUUID(),
      }
      storeApplicationFlow(linkId, updated)
      return updated
    })
  }, [linkId])

  const rebindFlowForNewResume = useCallback(() => {
    // The server requires idempotency_key == flow id whenever resume_url is
    // sent. After clearPendingFlowResume rotated the key, a re-attached resume
    // needs a flow whose key falls back to its own id.
    // ponytail: this splits analytics for this rare retry into a new flow, the
    // price of keeping the server's resume-proof rule.
    setFlow((current) =>
      current.idempotency_key && current.idempotency_key !== current.id
        ? createFreshApplicationFlow(linkId)
        : current
    )
  }, [linkId])

  return {
    applicationFlowId: context.application_flow_id,
    applicationIdempotencyKey: flow.idempotency_key ?? flow.id,
    applicationSource: context.source,
    analyticsHeaders,
    captureApplicationEvent,
    clearApplicationFlowId,
    markApplicationFlowHadResume,
    pendingFlowHadResume: flow.had_resume,
    clearPendingFlowResume,
    rebindFlowForNewResume,
  }
}
